import type { ScoringConfig } from "@/api/types";
import {
  CollapsibleSection,
  NumberFieldEditor,
  TagListEditor,
  humanizeScoringKey,
} from "./ScoringConfigEditors";

interface GlobalScoringConfigProps {
  config: ScoringConfig;
  /** Functional updater (the parent's setScoringConfig) — every edit
   *  reads the previous config so rapid sequential changes never clobber
   *  each other. */
  onChange: (updater: (prev: ScoringConfig) => ScoringConfig) => void;
  disabled?: boolean;
}

/** The non-dimension scoring config: keyword lists (title roles,
 *  sectors, incubators, France detection) plus the booster + stage
 *  duration scalars. Each block renders only when its key is present
 *  AND has the expected shape, so an unexpected wire value never paints
 *  an empty / garbled section. All blocks are collapsed by default —
 *  these are advanced knobs the operator opens on demand. */
export function GlobalScoringConfig({
  config,
  onChange,
  disabled,
}: GlobalScoringConfigProps) {
  function setKey<K extends keyof ScoringConfig>(
    key: K,
    value: ScoringConfig[K],
  ) {
    onChange((prev) => ({ ...prev, [key]: value }));
  }

  const titleClassification = isPlainObject(config.title_classification)
    ? (config.title_classification as Record<string, unknown>)
    : null;
  const stageDuration = isPlainObject(config.stage_duration_months)
    ? (config.stage_duration_months as Record<string, unknown>)
    : null;
  const hasBooster =
    typeof config.booster_new_round_days === "number" ||
    typeof config.booster_predicted_months_ahead === "number";

  const sections: Array<{ key: keyof ScoringConfig; title: string; desc: string }> =
    [
      {
        key: "excluded_incubators",
        title: "Excluded Incubators",
        desc: "Companies from these programs are excluded.",
      },
      {
        key: "tech_sectors",
        title: "Tech Sectors",
        desc: "Sectors that qualify a company as tech.",
      },
      {
        key: "blacklisted_sectors",
        title: "Blacklisted Sectors",
        desc: "Companies in these sectors are excluded.",
      },
      {
        key: "france_investor_keywords",
        title: "France Detection Keywords",
        desc: "Signals used to detect a link with France.",
      },
    ];

  // Nothing to show — e.g. M&A or a project type without v2 scoring.
  const hasAnything =
    !!titleClassification ||
    !!stageDuration ||
    hasBooster ||
    sections.some((s) => Array.isArray(config[s.key]));
  if (!hasAnything) return null;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium tracking-tight text-slate-900">
        Advanced scoring config
      </h3>

      {titleClassification && (
        <CollapsibleSection
          title="Title Classification"
          description="Role keywords matched against contact titles."
        >
          <div className="space-y-5">
            {Object.entries(titleClassification).map(([role, keywords]) => (
              <TagListEditor
                key={role}
                label={humanizeScoringKey(role)}
                tags={Array.isArray(keywords) ? (keywords as string[]) : []}
                disabled={disabled}
                onChange={(nextTags) =>
                  onChange((prev) => {
                    const tc = isPlainObject(prev.title_classification)
                      ? (prev.title_classification as Record<string, string[]>)
                      : {};
                    return {
                      ...prev,
                      title_classification: { ...tc, [role]: nextTags },
                    };
                  })
                }
              />
            ))}
          </div>
        </CollapsibleSection>
      )}

      {sections
        .filter((s) => Array.isArray(config[s.key]))
        .map((s) => (
          <CollapsibleSection key={s.key} title={s.title} description={s.desc}>
            <TagListEditor
              label={s.title}
              tags={(config[s.key] as string[] | undefined) ?? []}
              disabled={disabled}
              onChange={(next) =>
                setKey(s.key, next as ScoringConfig[typeof s.key])
              }
            />
          </CollapsibleSection>
        ))}

      {hasBooster && (
        <CollapsibleSection
          title="Booster Settings"
          description="Recent / predicted funding round detection."
        >
          <div className="space-y-1">
            {typeof config.booster_new_round_days === "number" && (
              <NumberFieldEditor
                label="New round detection window"
                value={config.booster_new_round_days}
                suffix="days"
                disabled={disabled}
                onChange={(n) => setKey("booster_new_round_days", n)}
              />
            )}
            {typeof config.booster_predicted_months_ahead === "number" && (
              <NumberFieldEditor
                label="Predicted round lookahead"
                value={config.booster_predicted_months_ahead}
                suffix="months"
                disabled={disabled}
                onChange={(n) => setKey("booster_predicted_months_ahead", n)}
              />
            )}
          </div>
        </CollapsibleSection>
      )}

      {stageDuration && (
        <CollapsibleSection
          title="Stage Duration"
          description="Typical months per stage, drives T-prévisionnel."
        >
          <div className="space-y-1">
            {Object.entries(stageDuration).map(([key, months]) => (
              <NumberFieldEditor
                key={key}
                label={humanizeScoringKey(key)}
                value={typeof months === "number" ? months : 0}
                suffix="months"
                disabled={disabled}
                onChange={(n) =>
                  onChange((prev) => {
                    const sd = isPlainObject(prev.stage_duration_months)
                      ? (prev.stage_duration_months as Record<string, number>)
                      : {};
                    return {
                      ...prev,
                      stage_duration_months: { ...sd, [key]: n },
                    };
                  })
                }
              />
            ))}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
